import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { signUp as signUpService } from '../services/authService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const currentUserIdRef = useRef(null)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      const newUserId = session?.user?.id ?? null

      // USER_UPDATED fires when email is confirmed (email_confirmed_at is set).
      // Reload the profile even if the userId hasn't changed so that
      // isEmailVerified reflects the newly-confirmed state.
      if (event === 'USER_UPDATED' && newUserId === currentUserIdRef.current) {
        setUser(session?.user ?? null)
        profileOperations?.load(newUserId) // Refresh to pick up email_verified_at
        setLoading(false)
        return
      }

      // Skip state updates if the same user is already set.
      // Supabase fires TOKEN_REFRESHED on every window-focus when the JWT
      // is near expiry. Without this guard, setUser creates a new object
      // reference → React re-renders every useAuth() consumer → every
      // dashboard refetches all its data.
      if (newUserId === currentUserIdRef.current) {
        setLoading(false)
        return
      }

      currentUserIdRef.current = newUserId
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName, testAccountCode, role, expertData) => {
    return await signUpService(email, password, fullName, testAccountCode, role, expertData)
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    // Whitelist: only allow safe display-level fields (C-05 / H-05).
    // Protected columns (role, twofa_*, is_test_account, is_suspended, etc.)
    // must be changed via dedicated admin RPCs or Edge Functions.
    const ALLOWED_FIELDS = new Set([
      'full_name', 'avatar_url', 'bio', 'title', 'phone',
      'website', 'linkedin_url', 'location', 'timezone',
      'notification_preferences', 'marketing_opt_in',
    ]);
    const safe = Object.fromEntries(
      Object.entries(updates).filter(([key]) => ALLOWED_FIELDS.has(key))
    );
    if (Object.keys(safe).length === 0) {
      return { error: { message: 'No updatable fields provided' } };
    }

    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(safe)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshProfile = async () => {
    if (user?.id) {
      await profileOperations?.load(user?.id)
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user,
    // Primary source of truth: auth.users.email_confirmed_at (set by Supabase the
    // moment the confirmation link is clicked, available on the session user object).
    // Fallback to user_profiles.email_verified_at for server-side consistency.
    isEmailVerified: !!user?.email_confirmed_at || !!userProfile?.email_verified_at,
    is2FAEnabled: !!userProfile?.twofa_enabled,
    hasCardOnFile: !!userProfile?.card_on_file_at,
    userRole: userProfile?.role || 'client'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
